import { useState, useRef, useEffect } from "react";
import { Ava } from "../atoms/Ava.jsx";
import { runStressTest, getStressTests, getStressTest, reviewPrompt, pollForCompletion } from "../services/stressTest.js";

// ── Verdict styling ────────────────────────────────────────────────────────────

const VERDICT_STYLES = {
  PASS: { bg: "#10b98118", border: "#10b98140", color: "#10b981", label: "PASS", icon: "✓" },
  WEAK: { bg: "#f59e0b18", border: "#f59e0b40", color: "#f59e0b", label: "WEAK", icon: "⚠" },
  FAIL: { bg: "#ef444418", border: "#ef444440", color: "#ef4444", label: "FAIL", icon: "✗" },
};

const RECOMMENDATION_STYLES = {
  GO: { bg: "#10b98120", border: "#10b98150", color: "#10b981", label: "GO" },
  CONDITIONAL: { bg: "#f59e0b20", border: "#f59e0b50", color: "#f59e0b", label: "CONDITIONAL" },
  "NO-GO": { bg: "#ef444420", border: "#ef444450", color: "#ef4444", label: "NO-GO" },
};

function VerdictBadge({ verdict, small }) {
  const s = VERDICT_STYLES[verdict] || VERDICT_STYLES.WEAK;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: small ? "2px 8px" : "4px 12px",
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 6, color: s.color,
      fontSize: small ? 10 : 12, fontWeight: 700,
    }}>
      {s.icon} {s.label}
    </span>
  );
}

function RecommendationBadge({ recommendation }) {
  const s = RECOMMENDATION_STYLES[recommendation] || RECOMMENDATION_STYLES["NO-GO"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "6px 16px", background: s.bg,
      border: `1px solid ${s.border}`, borderRadius: 8,
      color: s.color, fontSize: 14, fontWeight: 800,
    }}>
      {s.label}
    </span>
  );
}

// ── Evaluation Card ────────────────────────────────────────────────────────────

function EvalCard({ result, personaId, testId, onReviewSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [reviewVerdict, setReviewVerdict] = useState(result.human_verdict || "");
  const [reviewNote, setReviewNote] = useState(result.review_note || "");
  const [saving, setSaving] = useState(false);

  const effectiveVerdict = result.human_verdict
    ? (result.human_verdict === "AGREE" ? result.verdict : result.human_verdict.replace("OVERRIDE_", ""))
    : result.verdict;

  const handleSaveReview = async () => {
    if (!reviewVerdict || saving) return;
    setSaving(true);
    try {
      const resp = await reviewPrompt(personaId, testId, result.id, reviewVerdict, reviewNote);
      if (onReviewSaved) onReviewSaved(resp);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      margin: "4px 0", padding: "10px 14px",
      background: "#0d172490", border: `1px solid ${VERDICT_STYLES[effectiveVerdict]?.border || "#1e2d45"}`,
      borderRadius: 10, fontSize: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <span style={{ color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>{result.id}</span>
          <VerdictBadge verdict={effectiveVerdict} small />
          <span style={{ color: "#475569", fontSize: 11 }}>+{result.human_score ?? result.score}pt</span>
          {result.human_verdict && (
            <span style={{ color: "#8b5cf6", fontSize: 10, fontWeight: 600 }}>
              {result.human_verdict === "AGREE" ? "✓ Reviewed" : "⊘ Overridden"}
            </span>
          )}
        </div>
        <button onClick={() => setExpanded(!expanded)}
          style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>
          {expanded ? "▾" : "▸"}
        </button>
      </div>

      <div style={{ color: "#94a3b8", marginTop: 4, lineHeight: 1.5 }}>
        {result.reasoning}
      </div>

      {result.flags?.length > 0 && (
        <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {result.flags.map((f, i) => (
            <span key={i} style={{ padding: "2px 8px", background: "#ef444412", border: "1px solid #ef444430", borderRadius: 4, fontSize: 10, color: "#ef4444" }}>
              {f}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div style={{ marginTop: 10, borderTop: "1px solid #1e2d45", paddingTop: 10 }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, marginBottom: 4 }}>WATCH FOR</div>
          <div style={{ color: "#64748b", fontSize: 11, marginBottom: 10 }}>{result.watchFor}</div>

          {/* Review controls */}
          <div style={{ background: "#0a0f1a", border: "1px solid #1e2d45", borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 10, color: "#8b5cf6", fontWeight: 700, marginBottom: 6 }}>HUMAN REVIEW</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {["AGREE", "OVERRIDE_PASS", "OVERRIDE_WEAK", "OVERRIDE_FAIL"].map(v => (
                <button key={v} onClick={() => setReviewVerdict(v)}
                  style={{
                    padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit",
                    background: reviewVerdict === v ? "#8b5cf620" : "#0d1724",
                    border: `1px solid ${reviewVerdict === v ? "#8b5cf6" : "#1e2d45"}`,
                    color: reviewVerdict === v ? "#8b5cf6" : "#64748b",
                  }}>
                  {v.replace("OVERRIDE_", "→ ")}
                </button>
              ))}
            </div>
            <input
              value={reviewNote}
              onChange={e => setReviewNote(e.target.value)}
              placeholder="Review note (optional)..."
              style={{
                width: "100%", padding: "6px 10px", background: "#0d1724",
                border: "1px solid #1e2d45", borderRadius: 6,
                color: "#e2e8f0", fontSize: 11, fontFamily: "inherit",
                outline: "none", marginBottom: 6, boxSizing: "border-box",
              }}
            />
            <button onClick={handleSaveReview} disabled={!reviewVerdict || saving}
              style={{
                padding: "5px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: reviewVerdict && !saving ? "pointer" : "not-allowed",
                background: reviewVerdict && !saving ? "#8b5cf620" : "#1e2d45",
                border: `1px solid ${reviewVerdict && !saving ? "#8b5cf6" : "#1e2d4580"}`,
                color: reviewVerdict && !saving ? "#8b5cf6" : "#475569",
                fontFamily: "inherit",
              }}>
              {saving ? "Saving..." : "Save Review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary Card ───────────────────────────────────────────────────────────────

function SummaryCard({ summary }) {
  if (!summary) return null;
  const pct = summary.overall_max > 0
    ? Math.round((summary.overall_score / summary.overall_max) * 100)
    : 0;

  return (
    <div style={{
      margin: "12px 0", padding: 16,
      background: "#0a0f1a", border: "1px solid #1e2d45",
      borderRadius: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif" }}>
            {summary.overall_score}/{summary.overall_max} <span style={{ fontSize: 14, color: "#64748b" }}>({pct}%)</span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {summary.review_status === "reviewed" ? "Human-verified" : summary.review_status === "partial" ? "Partially reviewed" : "AI-scored"}
          </div>
        </div>
        <RecommendationBadge recommendation={summary.recommendation} />
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div style={{ padding: "6px 12px", background: "#10b98112", border: "1px solid #10b98130", borderRadius: 6, fontSize: 12 }}>
          <span style={{ color: "#10b981", fontWeight: 700 }}>{summary.pass_count}</span>
          <span style={{ color: "#475569" }}> pass</span>
        </div>
        <div style={{ padding: "6px 12px", background: "#f59e0b12", border: "1px solid #f59e0b30", borderRadius: 6, fontSize: 12 }}>
          <span style={{ color: "#f59e0b", fontWeight: 700 }}>{summary.weak_count}</span>
          <span style={{ color: "#475569" }}> weak</span>
        </div>
        <div style={{ padding: "6px 12px", background: "#ef444412", border: "1px solid #ef444430", borderRadius: 6, fontSize: 12 }}>
          <span style={{ color: "#ef4444", fontWeight: 700 }}>{summary.fail_count}</span>
          <span style={{ color: "#475569" }}> fail</span>
        </div>
      </div>

      {/* Category breakdown */}
      {summary.category_scores && Object.keys(summary.category_scores).length > 0 && (
        <div style={{ borderTop: "1px solid #1e2d45", paddingTop: 10 }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, marginBottom: 6 }}>CATEGORY SCORES</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {Object.entries(summary.category_scores).map(([cat, { score, max }]) => {
              const catPct = max > 0 ? Math.round((score / max) * 100) : 0;
              const catColor = catPct >= 83 ? "#10b981" : catPct >= 60 ? "#f59e0b" : "#ef4444";
              return (
                <div key={cat} style={{ padding: "6px 10px", background: "#0d1724", borderRadius: 6, border: "1px solid #1e2d45" }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{cat}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: catColor }}>{score}/{max} <span style={{ fontSize: 10, color: "#475569" }}>({catPct}%)</span></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Download helper ────────────────────────────────────────────────────────────

function downloadReport(summary, personaName) {
  if (!summary) return;
  const pct = summary.overall_max > 0 ? Math.round((summary.overall_score / summary.overall_max) * 100) : 0;
  let md = `# Stress Test Report — ${personaName}\n`;
  md += `**Date:** ${new Date(summary.created_at || Date.now()).toLocaleString()}\n`;
  md += `**Score:** ${summary.overall_score}/${summary.overall_max} (${pct}%)\n`;
  md += `**Recommendation:** ${summary.recommendation}\n`;
  md += `**Review Status:** ${summary.review_status || "unreviewed"}\n\n`;
  md += `| Pass | Weak | Fail |\n|------|------|------|\n| ${summary.pass_count} | ${summary.weak_count} | ${summary.fail_count} |\n\n`;

  if (summary.prompt_results) {
    md += `## Per-Prompt Results\n\n`;
    for (const r of summary.prompt_results) {
      const ev = r.human_verdict ? (r.human_verdict === "AGREE" ? r.verdict : r.human_verdict.replace("OVERRIDE_", "")) : r.verdict;
      md += `### ${r.id} — ${ev} (+${r.human_score ?? r.score}pt)\n`;
      md += `**Prompt:** ${r.prompt}\n\n`;
      md += `**Response:** ${r.response}\n\n`;
      md += `**AI Verdict:** ${r.reasoning}\n`;
      if (r.human_verdict) md += `**Human Review:** ${r.human_verdict}${r.review_note ? " — " + r.review_note : ""}\n`;
      md += `\n---\n\n`;
    }
  }

  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `stress-test-${personaName.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function StressTest({ persona }) {
  const [running, setRunning] = useState(false);
  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [testId, setTestId] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);

  const pc = persona.accentColor || persona.accent_color || "#3b82f6";

  const scroll = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

  // On mount: check if there's a running test and resume polling
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStressTests(persona.id, 1, 0);
        const latest = data.tests?.[0];
        if (latest?.recommendation === "RUNNING" && !cancelled) {
          setRunning(true);
          setTestId(latest.id);
          try {
            const completed = await pollForCompletion(persona.id, latest.id, {
              interval: 4000,
              onUpdate: (d) => {
                if (cancelled) return;
                if (d.prompt_results?.length) {
                  const msgs = [];
                  for (const r of d.prompt_results) {
                    msgs.push({ type: "prompt", id: r.id, text: r.prompt, category: r.category, risk: r.risk });
                    msgs.push({ type: "response", text: r.response });
                    msgs.push({ type: "eval", result: r });
                  }
                  setMessages(msgs);
                  setProgress({ index: d.prompt_results.length - 1, total: d.overall_max / 3 });
                }
              },
            });
            if (!cancelled) {
              setSummary(completed);
              const msgs = [];
              for (const r of (completed.prompt_results || [])) {
                msgs.push({ type: "prompt", id: r.id, text: r.prompt, category: r.category, risk: r.risk });
                msgs.push({ type: "response", text: r.response });
                msgs.push({ type: "eval", result: r });
              }
              setMessages(msgs);
            }
          } catch { /* ignore poll failure */ }
          if (!cancelled) setRunning(false);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [persona.id]);

  // Load history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await getStressTests(persona.id);
      setHistory(data.tests || []);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  // Load a previous test result
  const loadTest = async (tid) => {
    try {
      const data = await getStressTest(persona.id, tid);
      setTestId(data.id);
      setSummary(data);
      // Rebuild messages from prompt_results
      const msgs = [];
      for (const r of (data.prompt_results || [])) {
        msgs.push({ type: "prompt", id: r.id, text: r.prompt, category: r.category, risk: r.risk });
        msgs.push({ type: "response", text: r.response });
        msgs.push({ type: "eval", result: r });
      }
      setMessages(msgs);
      setShowHistory(false);
    } catch { /* ignore */ }
  };

  const startTest = async (promptIds) => {
    setRunning(true);
    setMessages([]);
    setProgress(null);
    setSummary(null);
    setTestId(null);
    setError(null);
    abortRef.current = new AbortController();

    try {
      const result = await runStressTest(persona.id, {
        promptIds,
        signal: abortRef.current.signal,
        onProgress: (data) => {
          setProgress(data);
          if (data.testId) setTestId(data.testId);
          // Add prompt message
          setMessages(prev => [...prev, {
            type: "prompt",
            id: data.promptId,
            text: data.promptId, // Will be updated by result event
            category: null,
          }]);
          scroll();
        },
        onResult: (data) => {
          setProgress(data);
          // Replace the placeholder prompt with real data, add response + eval
          setMessages(prev => {
            // Remove the last placeholder prompt for this ID
            const filtered = prev.filter((m, i) =>
              !(m.type === "prompt" && m.id === data.promptId && i === prev.length - 1)
            );
            return [
              ...filtered,
              { type: "prompt", id: data.result.id, text: data.result.prompt, category: data.result.category, risk: data.result.risk },
              { type: "response", text: data.result.response },
              { type: "eval", result: data.result },
            ];
          });
          scroll();
        },
        onCompleted: (data) => {
          setSummary(data);
          setTestId(data.testId);
          scroll();
        },
      });

      if (result?.polling && result.testId) {
        // SSE stream disconnected — fall back to polling
        setError(null);
        try {
          const completed = await pollForCompletion(persona.id, result.testId, {
            interval: 4000,
            onUpdate: (data) => {
              // Show partial progress from DB
              if (data.prompt_results?.length) {
                const msgs = [];
                for (const r of data.prompt_results) {
                  msgs.push({ type: "prompt", id: r.id, text: r.prompt, category: r.category, risk: r.risk });
                  msgs.push({ type: "response", text: r.response });
                  msgs.push({ type: "eval", result: r });
                }
                setMessages(msgs);
                setProgress({ index: data.prompt_results.length - 1, total: data.overall_max / 3 });
              }
            },
          });
          setSummary(completed);
          setTestId(completed.id);
          // Rebuild messages from completed results
          const msgs = [];
          for (const r of (completed.prompt_results || [])) {
            msgs.push({ type: "prompt", id: r.id, text: r.prompt, category: r.category, risk: r.risk });
            msgs.push({ type: "response", text: r.response });
            msgs.push({ type: "eval", result: r });
          }
          setMessages(msgs);
        } catch (pollErr) {
          setError(pollErr.message || "Failed to fetch completed results");
        }
      } else if (result && !summary) {
        setSummary(result);
        setTestId(result.testId);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Stress test failed");
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const stopTest = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const rerunWeak = () => {
    if (!summary?.prompt_results) return;
    const weakIds = summary.prompt_results
      .filter(r => (r.human_score ?? r.score) <= 1)
      .map(r => r.id);
    if (weakIds.length > 0) startTest(weakIds);
  };

  const handleReviewSaved = (resp) => {
    if (!resp?.ok || !resp.promptResult) return;
    // Update the per-prompt result in messages feed
    setMessages(prev => prev.map(m =>
      m.type === "result" && m.result?.id === resp.promptResult.id
        ? { ...m, result: { ...m.result, ...resp.promptResult } }
        : m
    ));
    // Update summary aggregates + per-prompt results
    setSummary(prev => {
      if (!prev) return prev;
      const updatedResults = (prev.prompt_results || []).map(r =>
        r.id === resp.promptResult.id ? { ...r, ...resp.promptResult } : r
      );
      return {
        ...prev,
        overall_score: resp.overall_score,
        recommendation: resp.recommendation,
        review_status: resp.review_status,
        reviewed_prompts_count: resp.reviewed_prompts_count,
        category_scores: resp.category_scores,
        prompt_results: updatedResults,
      };
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", maxWidth: 720, margin: "0 auto", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px", borderBottom: "1px solid #1e2d45",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#090e1a", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Ava ini={persona.avatar} color={pc} size={32} photoUrl={persona.photoUrl} />
          <div>
            <div style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 13 }}>{persona.name}</div>
            <div style={{ color: "#ef4444", fontSize: 10, fontWeight: 600 }}>STRESS TEST MODE</div>
          </div>
        </div>

        {/* Progress indicator */}
        {progress && running && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#64748b", fontSize: 11, fontFamily: "monospace" }}>
              {progress.promptId} [{(progress.index ?? 0) + 1}/{progress.total}]
            </span>
            <span style={{ color: pc, fontSize: 12, fontWeight: 700 }}>
              {progress.score ?? 0}/{progress.maxSoFar ?? 0}
            </span>
          </div>
        )}

        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
            style={{
              padding: "5px 10px", borderRadius: 6, fontSize: 11,
              background: "#0d1724", border: "1px solid #1e2d45",
              color: "#64748b", cursor: "pointer", fontFamily: "inherit",
            }}>
            History
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {running && progress && (
        <div style={{ height: 3, background: "#0d1724", flexShrink: 0 }}>
          <div style={{
            height: "100%",
            background: `linear-gradient(90deg, ${pc}, #8b5cf6)`,
            width: `${progress.total > 0 ? Math.round(((progress.index ?? 0) + 1) / progress.total * 100) : 0}%`,
            transition: "width .3s ease",
          }} />
        </div>
      )}

      {/* History panel */}
      {showHistory && (
        <div style={{
          padding: 12, background: "#0a0f1a", borderBottom: "1px solid #1e2d45",
          maxHeight: 200, overflow: "auto",
        }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, marginBottom: 8 }}>PAST TEST RUNS</div>
          {loadingHistory && <div style={{ color: "#64748b", fontSize: 11 }}>Loading...</div>}
          {!loadingHistory && history.length === 0 && <div style={{ color: "#64748b", fontSize: 11 }}>No previous tests</div>}
          {history.map(t => {
            const pct = t.overall_max > 0 ? Math.round((t.overall_score / t.overall_max) * 100) : 0;
            const recStyle = RECOMMENDATION_STYLES[t.recommendation] || RECOMMENDATION_STYLES["NO-GO"];
            return (
              <button key={t.id} onClick={() => loadTest(t.id)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "8px 10px", marginBottom: 4,
                  background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 6,
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                <div>
                  <span style={{ color: "#94a3b8", fontSize: 11 }}>
                    {new Date(t.created_at).toLocaleDateString()} {new Date(t.created_at).toLocaleTimeString()}
                  </span>
                  {t.review_status !== "unreviewed" && (
                    <span style={{ color: "#8b5cf6", fontSize: 10, marginLeft: 6 }}>
                      {t.review_status === "reviewed" ? "✓ Reviewed" : `${t.reviewed_prompts_count} reviewed`}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: recStyle.color, fontSize: 12, fontWeight: 700 }}>{t.overall_score}/{t.overall_max} ({pct}%)</span>
                  <span style={{ padding: "2px 8px", background: recStyle.bg, border: `1px solid ${recStyle.border}`, borderRadius: 4, color: recStyle.color, fontSize: 10, fontWeight: 700 }}>
                    {t.recommendation}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Messages feed */}
      <div className="qm-scrollable" style={{ flex: 1, minHeight: 0, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {messages.length === 0 && !running && (
          <div style={{ textAlign: "center", paddingTop: 40 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🛡️</div>
            <h3 style={{ color: "#f1f5f9", fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Persona Stress Test</h3>
            <p style={{ color: "#64748b", fontSize: 13, maxWidth: 400, margin: "0 auto 20px", lineHeight: 1.6 }}>
              Run a 23-prompt adversarial battery against this persona. Each response is scored by an AI evaluator.
              Review and override verdicts inline.
            </p>
          </div>
        )}

        {messages.map((m, i) => {
          if (m.type === "prompt") {
            return (
              <div key={`p-${i}`} style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <div style={{
                  maxWidth: "80%", padding: "10px 14px",
                  background: "#ef444412", border: "1px solid #ef444430",
                  borderRadius: "16px 16px 4px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 700, fontFamily: "monospace" }}>{m.id}</span>
                    {m.category && <span style={{ fontSize: 9, color: "#64748b", background: "#1e2d45", padding: "1px 6px", borderRadius: 3 }}>{m.category}</span>}
                    {m.risk && <span style={{ fontSize: 9, color: m.risk === "HIGH" ? "#ef4444" : "#f59e0b" }}>{m.risk}</span>}
                  </div>
                  <div style={{ color: "#e2e8f0", fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
                </div>
              </div>
            );
          }

          if (m.type === "response") {
            return (
              <div key={`r-${i}`} style={{ display: "flex", gap: 8 }}>
                <Ava ini={persona.avatar} color={pc} size={28} photoUrl={persona.photoUrl} />
                <div style={{
                  maxWidth: "80%", padding: "10px 14px",
                  background: "#0d1724", border: "1px solid #1e2d45",
                  borderRadius: "16px 16px 16px 4px",
                }}>
                  <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
                </div>
              </div>
            );
          }

          if (m.type === "eval") {
            return (
              <EvalCard key={`e-${i}`} result={m.result} personaId={persona.id}
                testId={testId} onReviewSaved={handleReviewSaved} />
            );
          }

          return null;
        })}

        {running && !messages.length && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ color: "#64748b", fontSize: 12 }}>Starting stress test...</div>
          </div>
        )}

        {summary && <SummaryCard summary={summary} />}
        {error && <div style={{ textAlign: "center", color: "#ef4444", fontSize: 13, padding: "10px 14px", background: "#ef444411", borderRadius: 7, border: "1px solid #ef444430" }}>{error}</div>}
        <div ref={bottomRef} />
      </div>

      {/* Controls */}
      <div style={{ padding: "10px 16px", paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))", background: "#090e1a", borderTop: "1px solid #1e2d45", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {!running ? (
            <>
              <button onClick={() => startTest()}
                style={{
                  flex: 1, padding: "12px 16px",
                  background: `linear-gradient(135deg, #ef4444, #ef4444bb)`,
                  color: "#fff", border: "none", borderRadius: 11,
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                {messages.length > 0 ? "Re-run Full Battery" : "Run Stress Test (23 prompts)"}
              </button>
              {summary && summary.weak_count + summary.fail_count > 0 && (
                <button onClick={rerunWeak}
                  style={{
                    padding: "12px 16px",
                    background: "#f59e0b18", border: "1px solid #f59e0b40",
                    borderRadius: 11, color: "#f59e0b",
                    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Re-run Weak/Fail ({summary.weak_count + summary.fail_count})
                </button>
              )}
              {summary && (
                <button onClick={() => downloadReport(summary, persona.name)}
                  style={{
                    padding: "12px 16px",
                    background: "#0d1724", border: "1px solid #1e2d45",
                    borderRadius: 11, color: "#64748b",
                    fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  ↓ Report
                </button>
              )}
            </>
          ) : (
            <button onClick={stopTest}
              style={{
                flex: 1, padding: "12px 16px",
                background: "#1e2d45", color: "#94a3b8",
                border: "1px solid #334155", borderRadius: 11,
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
              Stop Test
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StressTest;
